/**
 * NotificationItem Component
 * Renders individual notifications with all variants
 */

import React, { useEffect, useRef, useState, useCallback, type MouseEvent } from 'react';
import {
  Close,
  ErrorFilled,
  CheckmarkFilled,
  WarningFilled,
  InformationFilled,
  Launch,
  Popup,
} from '@core/icons-react';

import { Tag, seg } from '../../../../tags/Tag';
import { PrimaryButton } from '../../../../button/PrimaryButton';
import { Link } from '../../../Link';
import { NotificationItemProps, NotificationKind } from '../../types';
import { getAnimationStyles } from '../../utils/positioning';

/**
 * Icon mapping for notification kinds
 */
const iconTypes: Record<NotificationKind, React.ElementType> = {
  error: ErrorFilled,
  success: CheckmarkFilled,
  warning: WarningFilled,
  info: InformationFilled,
};

/**
 * NotificationItem Component
 */
export const NotificationItem: React.FC<NotificationItemProps> = ({
  id,
  type = 'toast',
  kind = 'info',
  title,
  subtitle,
  caption,
  children,
  notificationChild,
  hideCloseButton = false,
  lowContrast = false,
  onClose,
  onCloseButtonClick,
  onActionClick,
  actionLabel,
  role,
  statusIconDescription,
  closeAriaLabel = 'Close notification',
  animationState = 'idle',
  onAnimationComplete,
  index = 0,
  position = 'top-right',
}) => {
  const [internalAnimationState, setInternalAnimationState] = useState(animationState);
  const containerRef = useRef<HTMLDivElement>(null);
  const actionButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Get the appropriate icon
  const IconComponent = iconTypes[kind];

  // Update internal animation state when prop changes
  useEffect(() => {
    if (animationState !== internalAnimationState) {
      setInternalAnimationState(animationState);
      
      // Notify parent when animation completes
      if (onAnimationComplete) {
        const duration = animationState === 'entering' || animationState === 'exiting' ? 300 : 0;
        setTimeout(() => {
          onAnimationComplete(animationState);
        }, duration);
      }
    }
  }, [animationState, internalAnimationState, onAnimationComplete]);

  // Focus management for actionable notifications
  useEffect(() => {
    if (type === 'actionable' && role === 'alertdialog' && internalAnimationState === 'idle') {
      // Store previous focus
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      // Focus the action button or close button
      if (actionButtonRef.current) {
        actionButtonRef.current.focus();
      } else if (!hideCloseButton && containerRef.current) {
        const closeButton = containerRef.current.querySelector('.core-tertiary-button') as HTMLButtonElement;
        closeButton?.focus();
      }
    }

    // Restore focus on unmount
    return () => {
      if (type === 'actionable' && previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [type, role, hideCloseButton, internalAnimationState]);

  // Handle escape key for actionable notifications
  useEffect(() => {
    if (type === 'actionable' || type === 'toast') {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && containerRef.current?.contains(document.activeElement)) {
          handleClose(event as any);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [type]);

  // Handle close
  const handleClose = useCallback((event: MouseEvent | KeyboardEvent) => {
    if (!onClose || onClose(event as MouseEvent) !== false) {
      setInternalAnimationState('exiting');
      if (onAnimationComplete) {
        setTimeout(() => onAnimationComplete('exiting'), 300);
      }
    }
  }, [onClose, onAnimationComplete]);

  // Handle close button click
  const handleCloseButtonClick = useCallback((event: MouseEvent) => {
    if (onCloseButtonClick) {
      onCloseButtonClick(event);
    }
    handleClose(event);
  }, [onCloseButtonClick, handleClose]);

  // Handle action button click
  const handleActionClick = useCallback(() => {
    if (onActionClick) {
      onActionClick();
      // Auto-close after action
      handleClose(new MouseEvent('click') as any);
    }
  }, [onActionClick, handleClose]);

  // Build class names
  const baseClass = 'notification';
  const classNames = [
    baseClass,
    `${baseClass}--${type}`,
    `${baseClass}--${kind}`,
    internalAnimationState && `${baseClass}--${internalAnimationState}`,
    lowContrast && `${baseClass}--low-contrast`,
    hideCloseButton && `${baseClass}--hide-close-button`,
  ].filter(Boolean).join(' ');

  // Get animation styles
  const animationStyles = getAnimationStyles(
    internalAnimationState,
    position,
    300
  );

  // Determine the appropriate role
  const notificationRole = role || (
    type === 'actionable' ? 'alertdialog' : 
    type === 'toast' ? 'status' : 
    'status'
  );

  return (
    <div
      ref={containerRef}
      className={classNames}
      role={notificationRole}
      aria-labelledby={title ? `${id}-title` : undefined}
      aria-describedby={subtitle ? `${id}-subtitle` : undefined}
      style={animationStyles}
    >
      {/* Main content area - icon + content */}
      <div className={`${baseClass}__main-content`}>
        {/* Icon */}
        {IconComponent && (
          <div className={`${baseClass}__icon-container`}>
            <IconComponent 
              className={`${baseClass}__icon`}
              size={14}
              aria-label={statusIconDescription || `${kind} icon`}
            />
          </div>
        )}

        {/* Content */}
        <div className={`${baseClass}__content`}>
          {title && (
            <div id={`${id}-title`} className={`${baseClass}__title`}>
              {title}
            </div>
          )}
          
          {subtitle && (
            <div id={`${id}-subtitle`} className={`${baseClass}__subtitle`}>
              {subtitle}
            </div>
          )}
          
          {caption && type === 'toast' && (
            <div className={`${baseClass}__caption`}>
              {caption}
            </div>
          )}
          
          {children && (
            <div className={`${baseClass}__children`}>
              {children}
            </div>
          )}

          {/* Render notificationChild (takes precedence over children) */}
          {notificationChild && (
            <div className={`${baseClass}__notification-child`}>
              {notificationChild.type === 'button' && (
                <PrimaryButton
                  variant="default"
                  title={notificationChild.label}
                  onClick={notificationChild.onClick}
                  size="small"
                  fullWidth={false}
                  align="center"
                />
              )}
              
              {notificationChild.type === 'link' && (
                <Link
                  size="medium"
                  color="strong"
                  underline="none"
                  weight="default"
                  icon={notificationChild.icon === 'launch' ? <Launch /> : notificationChild.icon === 'popup' ? <Popup /> : undefined}
                  iconPosition="right"
                  href={notificationChild.href || notificationChild.persistentHref || notificationChild.destination || '#'}
                  onClick={notificationChild.onClick || ((e) => e.preventDefault())}
                >
                  {notificationChild.label}
                </Link>
              )}
            </div>
          )}

          {/* Action button for actionable notifications (legacy pattern) */}
          {!notificationChild && type === 'actionable' && actionLabel && (
            <button
              ref={actionButtonRef}
              className={`${baseClass}__action-button`}
              onClick={handleActionClick}
              type="button"
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>

      {/* Close button - separate container with reduced padding */}
      {!hideCloseButton && (
        <div className={`${baseClass}__close-area`}>
          <Tag
            template="action-sm"
            segments={[seg.icon(Close, { size: 16 })]}
            onClick={handleCloseButtonClick}
            title={closeAriaLabel}
          />
        </div>
      )}
    </div>
  );
};
